/**
 * Helpers shared by the Stammdaten page and its form dialog.
 *
 * The interesting part is date handling: CMDBuild hands dates back in whichever
 * shape the underlying column has ('2026-08-19', '19/08/2026 00:00:00', epoch
 * millis, full ISO), while `<input type="date">` accepts *only* 'YYYY-MM-DD' and
 * `<input type="datetime-local">` only 'YYYY-MM-DDTHH:mm'. Feeding an unconverted
 * value to either silently renders an EMPTY input — which then submits as "clear
 * this field" and destroys the stored date. `toDateInput` / `toDateTimeInput` are
 * therefore mandatory on every date field, and they return '' only when the value
 * really is unusable.
 */

import type { CmdbAttribute, CmdbCardRow, CmdbClassMeta, CmdbOption } from '../../lib/types';

/** How many attribute columns the list table shows before the detail row. */
export const LIST_COLUMNS = 5;

/** 'dd/MM/yyyy[ HH:mm[:ss]]' — CMDBuild's localised shape. */
const DMY = /^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/;

function parts(value: unknown): { date: string; time: string } | null {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'number' || /^\d{12,}$/.test(String(value))) {
    const millis = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(millis)) return null;
    const parsed = new Date(millis);
    if (Number.isNaN(parsed.getTime())) return null;
    // Local time on purpose: the input shows what the user's clock shows.
    const pad = (n: number): string => String(n).padStart(2, '0');
    return {
      date: `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`,
      time: `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`,
    };
  }

  const text = String(value).trim();
  const dmy = DMY.exec(text);
  if (dmy) {
    const [, dd, mm, yyyy, hh = '', min = ''] = dmy;
    return { date: `${yyyy}-${mm}-${dd}`, time: hh && min ? `${hh}:${min}` : '' };
  }
  const iso = /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(text);
  if (iso) {
    const [, date = '', hh, min] = iso;
    return { date, time: hh && min ? `${hh}:${min}` : '' };
  }
  return null;
}

/** 'YYYY-MM-DD' for `<input type="date">`, or '' when unparsable. */
export function toDateInput(value: unknown): string {
  return parts(value)?.date ?? '';
}

/** 'YYYY-MM-DDTHH:mm' for `<input type="datetime-local">`, or '' when unparsable. */
export function toDateTimeInput(value: unknown): string {
  const split = parts(value);
  if (!split) return '';
  return `${split.date}T${split.time || '00:00'}`;
}

/** The form value for one attribute of a card (or the empty value for a new one). */
export function initialValue(attr: CmdbAttribute, row: CmdbCardRow | null): string | boolean {
  const raw = row?.values[attr.name];
  switch (attr.type) {
    case 'boolean':
      return raw === true;
    case 'date':
      return toDateInput(raw);
    case 'dateTime':
      return toDateTimeInput(raw);
    case 'reference':
    case 'lookup':
      return raw === null || raw === undefined ? '' : String(raw);
    default:
      return raw === null || raw === undefined ? '' : String(raw);
  }
}

/** All form values for a class, ready for `useState`. */
export function initialForm(
  meta: CmdbClassMeta,
  row: CmdbCardRow | null,
): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = {};
  for (const attr of meta.attributes) out[attr.name] = initialValue(attr, row);
  return out;
}

/**
 * Turn form state into the request body.
 *
 * System attributes are skipped (the server drops them anyway, and sending them
 * would make the diff misleading). References are sent as numbers, everything
 * else as the string the input produced — the server coerces and validates.
 */
export function toRequestBody(
  meta: CmdbClassMeta,
  form: Record<string, string | boolean>,
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const attr of meta.attributes) {
    if (attr.system) continue;
    const value = form[attr.name];
    if (attr.type === 'boolean') {
      body[attr.name] = value === true;
    } else if (attr.type === 'reference') {
      const text = String(value ?? '').trim();
      body[attr.name] = text === '' ? '' : Number(text);
    } else {
      body[attr.name] = String(value ?? '');
    }
  }
  return body;
}

/** The attributes shown as table columns: the first few editable, non-text ones. */
export function listAttributes(meta: CmdbClassMeta): CmdbAttribute[] {
  const preferred = meta.attributes.filter((a) => a.type !== 'text');
  return (preferred.length ? preferred : meta.attributes).slice(0, LIST_COLUMNS);
}

/** Every class a form for `meta` needs picker options for. */
export function referenceTargets(meta: CmdbClassMeta): string[] {
  const out = new Set<string>();
  for (const attr of meta.attributes) {
    if (attr.type === 'reference' && attr.target_class && !attr.system) out.add(attr.target_class);
  }
  return [...out];
}

/**
 * The card a reference attribute points at, or `null` when the attribute is not
 * a reference or is unset.
 *
 * `row.values` holds the raw card id for references (`row.display` holds only the
 * resolved German label), which is exactly what a deep link into this page needs.
 */
export function referenceLink(
  attr: CmdbAttribute,
  row: CmdbCardRow,
): { klass: string; id: number } | null {
  if (attr.type !== 'reference' || !attr.target_class) return null;
  const raw = row.values[attr.name];
  if (raw === null || raw === undefined || raw === '') return null;
  const id = typeof raw === 'number' ? raw : Number(String(raw).trim());
  if (!Number.isInteger(id) || id <= 0) return null;
  return { klass: attr.target_class, id };
}

/**
 * Version references of a product instance and the predecessor / base version
 * of a product version only offer the versions of the selected product;
 * without a product there is nothing to choose from.
 * Maps class → dependent reference → the product reference it follows.
 */
const PRODUCT_SCOPED_REFERENCES: Record<string, Record<string, string>> = {
  ProduktInstanz: { AusgelieferteVersionID: 'ProduktTypeID', AktuelleVersionID: 'ProduktTypeID' },
  ProduktVersion: { VorgaengerVersionID: 'ProduktTypeID', BaseVersionID: 'ProduktTypeID' },
};

/** The form field a reference's options depend on, if any. */
export function productReferenceOf(klass: string, attr: string): string | undefined {
  return PRODUCT_SCOPED_REFERENCES[klass]?.[attr];
}

/** Reference fields to re-check when *attr* (a product reference) changes. */
export function productScopedReferences(klass: string, attr: string): string[] {
  return Object.entries(PRODUCT_SCOPED_REFERENCES[klass] ?? {})
    .filter(([, product]) => product === attr)
    .map(([dependent]) => dependent);
}

/** Versions belonging to *productId* (a form value, '' = none selected). */
export function versionsOfProduct(list: readonly CmdbOption[], productId: string): CmdbOption[] {
  if (productId === '') return [];
  return list.filter((option) => String(option.product_id ?? '') === productId);
}

/**
 * Fields that follow another field while a card is created: a new instance
 * runs the version it was delivered with. Maps class → field → source field.
 */
const CREATE_FOLLOWERS: Record<string, Record<string, string>> = {
  ProduktInstanz: { AktuelleVersionID: 'AusgelieferteVersionID' },
};

/**
 * Form values after *source* changed from *previous*: a follower that is empty
 * or still shows the previous value takes the new one; a value the user set
 * differently stays.
 */
export function followCreateDefaults<T>(
  klass: string, source: string, previous: T | undefined, form: Record<string, T>,
): Record<string, T> {
  const next = { ...form };
  for (const [field, followed] of Object.entries(CREATE_FOLLOWERS[klass] ?? {})) {
    if (followed !== source) continue;
    const current = next[field];
    const value = form[source];
    if (value !== undefined && (current === undefined || current === '' || current === previous)) next[field] = value;
  }
  return next;
}

/** A card never references itself, e.g. a version is not its own predecessor. */
export function withoutOwnCard(
  list: readonly CmdbOption[], targetClass: string, klass: string, ownId: number | null,
): readonly CmdbOption[] {
  return ownId !== null && targetClass === klass ? list.filter((option) => option.id !== ownId) : list;
}
